import { ReactNode, useMemo } from "react";
import { Badge, Group, Select, Switch, Text } from "@mantine/core";
import { Configure, InstantSearch, useHits, useInstantSearch } from "react-instantsearch";
import { CtcPoStatusCode, CTC_PO_STATUS_TAGS } from "../../../../shared/ctcLifecycle";
import { CTC_ORDER_SORT_FIELDS, ctcOrderSearchFilter, ctcSortedIndex } from "../../../../shared/ctcSearch";
import { useCtcUser } from "./ctc_helpers";
import { CtcPanel } from "./ctc_panel";
import { CtcSignatureButton } from "./ctc_profile";
import { ColumnKey, ListView, ListViewButton, OrdersTable, useListView, useMemoryState, useWatchLanes } from "./ctc_orders_table";
import {
    CTC_ORDERS_INDEX, CtcDayRange as DayRange, CtcSearchLiveRefresh, CtcSearchError, CtcSearchInput, CtcSearchPager, CtcSearchRefresh,
    ctcOrderFromHit, useCtcSearchClient, useCtcSearchMemory,
} from "./ctc_search";

/*
 * Watch list and Purchase orders on Typesense (the Firestore versions are in ctc_orders.tsx): one search box
 * (reference, client reference, beneficiary number), status and created-date filters, any sort over all
 * matching orders, 25-250 per page. Live: they search again when functions-ctc rings their bell (CtcSearchLiveRefresh).
 */

const ORDER_QUERY_BY = "referenceNumber,clientRefNo,beneficiaryNumber";

/** Status filter (Purchase orders and Watch list). */
export function StatusSelect({ value, onChange }: { value: string | null; onChange: (v: string | null) => void }) {
    return (
        <Select placeholder="Any status" clearable value={value} onChange={onChange} w={220} comboboxProps={{ width: 300 }}
            data={Object.values(CtcPoStatusCode).map((code) => ({ value: code, label: CTC_PO_STATUS_TAGS[code].title }))} />
    );
}

function OrdersResults({ columns, emptyText }: { columns: ColumnKey[]; emptyText: string }) {
    const { items } = useHits();
    const { status } = useInstantSearch();
    const orders = useMemo(() => items.map((h) => ctcOrderFromHit(h as unknown as Record<string, unknown>)), [items]);
    return <OrdersTable orders={orders} loading={status !== "idle"} columns={columns} emptyText={emptyText} />;
}

function TypesenseList({ list, view, filters, toolbarLeft, toolbarRight, emptyText, onUseFirestore }: {
    list: string; view: ListView; filters: string; toolbarLeft: ReactNode; toolbarRight: ReactNode; emptyText: string;
    onUseFirestore: () => void;
}) {
    const client = useCtcSearchClient(ORDER_QUERY_BY);
    const indexName = ctcSortedIndex(CTC_ORDERS_INDEX, CTC_ORDER_SORT_FIELDS[view.sort.field], view.sort.dir);
    const memory = useCtcSearchMemory(list, indexName);
    return (
        // A new sort is a new index name: start a fresh search with it (search box and page size kept)
        <InstantSearch key={indexName} searchClient={client} indexName={indexName} initialUiState={memory.initialUiState} onStateChange={memory.onStateChange}>
            <Configure filters={filters} />
            <CtcSearchLiveRefresh kind="orders" />
            {/* Filters on the left; actions together on the right (they wrap as one block on narrow screens) */}
            <Group justify="space-between" align="center" gap="sm" mb="md">
                <Group gap="sm" align="center">{toolbarLeft}</Group>
                <Group gap="xs" align="center" ml="auto" wrap="nowrap">{toolbarRight}</Group>
            </Group>
            <CtcSearchError onUseFirestore={onUseFirestore} />
            <OrdersResults columns={view.columns} emptyText={emptyText} />
            <CtcSearchPager />
        </InstantSearch>
    );
}

/** All purchase orders (Typesense). */
export const CtcOrdersTypesense = ({ backendSwitch, onUseFirestore }: { backendSwitch: ReactNode; onUseFirestore: () => void }) => {
    const [status, setStatus] = useMemoryState<string | null>("ts:orders:status", null);
    const [range, setRange] = useMemoryState<DayRange>("ts:orders:range", [null, null]);
    const view = useListView("orders", { field: "created", dir: "desc" });
    const filters = ctcOrderSearchFilter({ status, from: range[0], to: range[1] });
    return (
        <CtcPanel>
            <TypesenseList
                list="orders" view={view} filters={filters} onUseFirestore={onUseFirestore}
                emptyText="No purchase orders match."
                toolbarLeft={<>
                    <CtcSearchInput placeholder="Reference, client reference or beneficiary no." />
                    <StatusSelect value={status} onChange={setStatus} />
                </>}
                toolbarRight={<>
                    {backendSwitch}
                    <ListViewButton view={view} note="Sorts all orders that match the search and filters." dateRange={{ value: range, onChange: setRange }} />
                    <CtcSearchRefresh />
                </>}
            />
        </CtcPanel>
    );
};

/** Work queue (Typesense): orders waiting on one of the user's lanes; searched again every 30 seconds. */
export const CtcWatchListTypesense = ({ backendSwitch, onUseFirestore }: { backendSwitch: ReactNode; onUseFirestore: () => void }) => {
    const user = useCtcUser();
    const lanes = useWatchLanes(user);
    const [onlyMine, setOnlyMine] = useMemoryState("watch:onlyMine", false);
    const [status, setStatus] = useMemoryState<string | null>("watch:status", null);
    const [range, setRange] = useMemoryState<DayRange>("ts:watch:range", [null, null]);
    const view = useListView("watch", { field: "created", dir: "asc" }); // oldest waiting first
    const filters = ctcOrderSearchFilter({ status, lanes, dealerEmail: onlyMine && !user.customerId ? user.email : null, from: range[0], to: range[1] });
    return (
        <CtcPanel>
            <Group gap="xs" mb="sm">
                <Text size="sm" c="dimmed">Awaiting action from:</Text>
                {lanes.map((lane) => <Badge key={lane} variant="light">{lane}</Badge>)}
                {lanes.length === 0 && <Text size="sm" c="dimmed">No CTC role assigned</Text>}
            </Group>
            <TypesenseList
                list="watch" view={view} filters={filters} onUseFirestore={onUseFirestore}
                emptyText="Nothing is waiting on you."
                toolbarLeft={<>
                    <CtcSearchInput placeholder="Reference, client reference or beneficiary no." />
                    <StatusSelect value={status} onChange={setStatus} />
                    {user.has("dealer") && !user.customerId && (
                        <Switch label="Only orders assigned to me" checked={onlyMine} onChange={(e) => setOnlyMine(e.currentTarget.checked)} />
                    )}
                </>}
                toolbarRight={<>
                    {backendSwitch}
                    <ListViewButton view={view} note="Sorts the whole queue of waiting orders." dateRange={{ value: range, onChange: setRange }} />
                    <CtcSearchRefresh />
                    <CtcSignatureButton />
                </>}
            />
        </CtcPanel>
    );
};
